import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { ApiError } from '@/api/client'
import { locationsApi } from '@/api/endpoints'
import type { Location, LocationInput } from '@/api/types'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { TextField } from '@/components/ui/Field'
import { Modal } from '@/components/ui/Modal'
import { useToast } from '@/components/ui/Toast'
import { slugify } from '@/lib/slug'

interface Props {
  open: boolean
  onClose: () => void
  /** `null` oznacza tworzenie nowego oddziału. */
  location: Location | null
}

const EMPTY: LocationInput = {
  name: '',
  slug: '',
  city: '',
  address: '',
  postal_code: '',
  phone: '',
  email: '',
}

export function LocationFormModal({ open, onClose, location }: Props) {
  const queryClient = useQueryClient()
  const { notifySuccess } = useToast()
  const isEdit = location !== null

  const [form, setForm] = useState<LocationInput>(EMPTY)
  const [slugTouched, setSlugTouched] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (!open) return
    setError(null)
    setFieldErrors({})
    setSlugTouched(isEdit)
    setForm(
      location
        ? {
            name: location.name,
            slug: location.slug,
            city: location.city ?? '',
            address: location.address ?? '',
            postal_code: location.postal_code ?? '',
            phone: location.phone ?? '',
            email: location.email ?? '',
          }
        : EMPTY,
    )
  }, [open, location, isEdit])

  const update = (field: keyof LocationInput, value: string) => {
    setForm((current) => ({ ...current, [field]: value }))
  }

  const mutation = useMutation({
    mutationFn: (payload: LocationInput) => {
      // Slug jest niezmienny po utworzeniu — stanowi trwały adres oddziału.
      const { slug, ...editable } = payload
      const cleaned = Object.fromEntries(
        Object.entries(editable).map(([key, value]) => [key, value === '' ? null : value]),
      ) as Partial<LocationInput>

      return isEdit
        ? locationsApi.update(location.id, cleaned)
        : locationsApi.create({ ...cleaned, name: payload.name, slug })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['locations'] })
      notifySuccess(isEdit ? 'Zapisano zmiany oddziału.' : 'Oddział został utworzony.')
      onClose()
    },
    onError: (caught: unknown) => {
      if (caught instanceof ApiError) {
        setError(caught.message)
        setFieldErrors(caught.fieldErrors)
      } else {
        setError('Nie udało się zapisać oddziału.')
      }
    },
  })

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    setError(null)
    setFieldErrors({})
    mutation.mutate({
      ...form,
      slug: slugTouched ? form.slug : slugify(form.name),
    })
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edytuj oddział' : 'Nowy oddział'}
      description={
        isEdit
          ? 'Zmiany zobaczą wszyscy użytkownicy przypisani do tego oddziału.'
          : 'Oddział jest podstawową granicą dostępu do danych w systemie.'
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Anuluj
          </Button>
          <Button type="submit" form="location-form" isLoading={mutation.isPending}>
            {isEdit ? 'Zapisz zmiany' : 'Utwórz oddział'}
          </Button>
        </>
      }
    >
      <form id="location-form" onSubmit={handleSubmit} className="space-y-5" noValidate>
        {error && <Alert tone="error">{error}</Alert>}

        <TextField
          label="Nazwa oddziału"
          value={form.name}
          onChange={(event) => update('name', event.target.value)}
          placeholder="np. Bytom"
          error={fieldErrors.name}
          required
          autoFocus
        />

        {/* Przy edycji sluga nie da się zmienić — jest trwałym identyfikatorem
            oddziału — więc pole nie pojawia się wcale zamiast stać nieaktywne. */}
        {!isEdit && (
          <TextField
            label="Identyfikator (slug)"
            value={slugTouched ? (form.slug ?? '') : slugify(form.name)}
            onChange={(event) => {
              setSlugTouched(true)
              update('slug', event.target.value)
            }}
            hint="Małe litery, cyfry i myślniki. Uzupełnia się automatycznie z nazwy."
            error={fieldErrors.slug}
            required
          />
        )}

        <div className="grid gap-5 sm:grid-cols-2">
          <TextField
            label="Miasto"
            value={form.city ?? ''}
            onChange={(event) => update('city', event.target.value)}
            error={fieldErrors.city}
          />
          <TextField
            label="Kod pocztowy"
            value={form.postal_code ?? ''}
            onChange={(event) => update('postal_code', event.target.value)}
            placeholder="41-902"
            error={fieldErrors.postal_code}
          />
        </div>

        <TextField
          label="Adres"
          value={form.address ?? ''}
          onChange={(event) => update('address', event.target.value)}
          placeholder="ul. Muzyczna 5"
          error={fieldErrors.address}
        />

        <div className="grid gap-5 sm:grid-cols-2">
          <TextField
            label="Telefon"
            type="tel"
            value={form.phone ?? ''}
            onChange={(event) => update('phone', event.target.value)}
            placeholder="+48 123 456 789"
            error={fieldErrors.phone}
          />
          <TextField
            label="E-mail"
            type="email"
            value={form.email ?? ''}
            onChange={(event) => update('email', event.target.value)}
            placeholder="bytom@legato.edu.pl"
            error={fieldErrors.email}
          />
        </div>
      </form>
    </Modal>
  )
}
